'use client';

import Link from "next/link";
import { Microscope, Code2, BarChart3, Server, ArrowRight, Beaker, FileCode, PieChart, Network } from "lucide-react";
import { LandingNav } from "@/components/LandingNav";
import { useLanguage } from "@/lib/LanguageContext";

export default function HomePage() {
    const { t } = useLanguage();
    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50">
            {/* Simple Top Navigation */}
            <LandingNav />

            {/* Hero Section */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-32 pb-16 sm:pb-20">
                <div className="text-center animate-fade-in">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium mb-8 border border-emerald-200">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        {t('landing.badge')}
                    </div>

                    {/* Masthead — academic journal framing */}
                    <div className="mb-8 sm:mb-10">
                        <div className="slogan-eyebrow" aria-hidden="true">
                            <span className="slogan-section">§ I</span>
                            <span className="slogan-eyebrow-divider" aria-hidden="true" />
                            <span>{t('landing.heroEyebrow')}</span>
                            <span className="slogan-cursor" />
                        </div>

                        <h1 className="mt-5 sm:mt-6 leading-[0.96]">
                            <span className="slogan-line1 block text-xl sm:text-2xl md:text-[1.65rem]">
                                {t('landing.heroTitle1')}
                            </span>
                            <span className="slogan-line2 slogan-headline block mt-2 sm:mt-3 text-[2.65rem] leading-[1.02] sm:text-6xl md:text-[6.25rem]">
                                {t('landing.heroTitle2')}
                            </span>
                        </h1>

                        <div className="slogan-rule-pair" aria-hidden="true">
                            <span className="slogan-rule" />
                            <span className="slogan-monogram">· D · R · A · W ·</span>
                            <span className="slogan-rule" />
                        </div>
                    </div>

                    {/* Subtitle */}
                    <p className="slogan-subtitle max-w-2xl mx-auto mb-10 px-4">
                        {t('landing.heroSubtitle')}
                    </p>

                    {/* CTA Buttons */}
                    <div className="slogan-cta flex flex-col sm:flex-row items-center justify-center gap-4 mb-20 px-4">
                        <Link
                            href="/agent"
                            className="group w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-semibold hover:from-emerald-700 hover:to-cyan-700 transition-all hover:scale-105 shadow-xl hover:shadow-2xl hover:shadow-emerald-200 flex items-center justify-center gap-2"
                        >
                            {t('landing.startCollaborating')}
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            href="#roles"
                            className="w-full sm:w-auto px-8 py-4 rounded-full bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-all border-2 border-gray-200 hover:border-emerald-300 hover:text-emerald-700 text-center"
                        >
                            {t('landing.learnMore')}
                        </Link>
                    </div>
                </div>

                {/* Role-Based Feature Cards */}
                <div id="roles" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-16 sm:mt-20">
                    {/* Wet Lab Scientists */}
                    <div className="group relative p-6 sm:p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-pink-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                        <div className="absolute inset-0 bg-gradient-to-br from-pink-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                        <div className="relative">
                            <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white group-hover:scale-110 transition-transform shadow-lg">
                                <Microscope className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('landing.wetLabTitle')}</h3>
                            <p className="text-gray-600 leading-relaxed">
                                {t('landing.wetLabDesc')}
                            </p>
                        </div>
                    </div>

                    {/* Computational Biologists */}
                    <div className="group relative p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                        <div className="relative">
                            <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white group-hover:scale-110 transition-transform shadow-lg">
                                <Code2 className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('landing.compBioTitle')}</h3>
                            <p className="text-gray-600 leading-relaxed">
                                {t('landing.compBioDesc')}
                            </p>
                        </div>
                    </div>

                    {/* Data Analysts */}
                    <div className="group relative p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-purple-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                        <div className="relative">
                            <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white group-hover:scale-110 transition-transform shadow-lg">
                                <BarChart3 className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('landing.dataAnalystTitle')}</h3>
                            <p className="text-gray-600 leading-relaxed">
                                {t('landing.dataAnalystDesc')}
                            </p>
                        </div>
                    </div>

                    {/* IT & Infrastructure */}
                    <div className="group relative p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-orange-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                        <div className="relative">
                            <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white group-hover:scale-110 transition-transform shadow-lg">
                                <Server className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('landing.itTitle')}</h3>
                            <p className="text-gray-600 leading-relaxed">
                                {t('landing.itDesc')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Platform Capabilities Section */}
                <div className="mt-32">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            {t('landing.modulesTitle')}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {t('landing.modulesSubtitle')}
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">

                        {/* Data Module - D */}
                        <div className="group relative p-6 rounded-xl bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
                            <div className="w-12 h-12 mb-4 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white text-xl font-bold">
                                D
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.dataModule')}</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">
                                {t('landing.dataModuleDesc')}
                            </p>
                        </div>

                        {/* Lab Records Module - R */}
                        <div className="group relative p-6 rounded-xl bg-white border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
                            <div className="w-12 h-12 mb-4 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xl font-bold">
                                R
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.registryModule')}</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">
                                {t('landing.registryModuleDesc')}
                            </p>
                        </div>

                        {/* Agents Module - A */}
                        <div className="group relative p-6 rounded-xl bg-white border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all">
                            <div className="w-12 h-12 mb-4 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                                A
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.agentsModule')}</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">
                                {t('landing.agentsModuleDesc')}
                            </p>
                        </div>

                        {/* Workflows Module - W */}
                        <div className="group relative p-6 rounded-xl bg-white border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                            <div className="w-12 h-12 mb-4 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
                                W
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.workflowsModule')}</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">
                                {t('landing.workflowsModuleDesc')}
                            </p>
                        </div>

                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="mt-24 text-center">
                    <p className="text-gray-600 text-lg mb-6">
                        {t('landing.ctaQuestion')}
                    </p>
                    <Link
                        href="/agent"
                        className="inline-flex items-center gap-2 px-8 sm:px-10 py-4 sm:py-5 rounded-full bg-gradient-to-r from-emerald-600 to-cyan-600 text-white text-base sm:text-lg font-semibold hover:from-emerald-700 hover:to-cyan-700 transition-all hover:scale-105 shadow-xl sm:shadow-2xl"
                    >
                        {t('common.getStarted')}
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-gray-200 bg-white/50 backdrop-blur-sm mt-20">
                <div className="max-w-7xl mx-auto px-6 py-8 text-center text-gray-600 text-sm">
                    <p className="text-center text-slate-400 text-sm mt-6">
                        {t('landing.footer')}
                    </p>
                </div>
            </footer>
        </div>
    );
}
